import { Editor } from '@tiptap/core';
import type { JSONContent } from '@tiptap/core';
import {
  contentTypeForDocFormat,
  detectDocFormat,
  EMPTY_JDOC_DOC,
  type DocFormat
} from './docTypes';
import { createMarkdownEditorExtensions } from './markdownEditorExtensions';

export class DocCodecError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DocCodecError';
  }
}

type MarkdownStorage = {
  markdown: {
    getMarkdown: () => string;
  };
};

function createCodecEditor(): Editor {
  return new Editor({
    extensions: createMarkdownEditorExtensions(),
    content: ''
  });
}

export function parseJdocJson(raw: string): JSONContent {
  const trimmed = raw.trim();
  if (!trimmed) return { ...EMPTY_JDOC_DOC };

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new DocCodecError('JDoc file is not valid JSON.');
  }

  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    (parsed as { type?: unknown }).type !== 'doc' ||
    !Array.isArray((parsed as { content?: unknown }).content)
  ) {
    throw new DocCodecError('JDoc file must contain a TipTap doc root.');
  }

  return parsed as JSONContent;
}

export function serializeJdocJson(doc: JSONContent): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

export function parseMarkdownToJson(markdown: string): JSONContent {
  const editor = createCodecEditor();
  try {
    editor.commands.setContent(markdown);
    return editor.getJSON();
  } finally {
    editor.destroy();
  }
}

export function serializeJsonToMarkdown(doc: JSONContent): string {
  const editor = createCodecEditor();
  try {
    editor.commands.setContent(doc);
    return (editor.storage as unknown as MarkdownStorage).markdown.getMarkdown();
  } finally {
    editor.destroy();
  }
}

export function parseStorageContent(path: string, raw: string): { format: DocFormat; doc: JSONContent } {
  const format = detectDocFormat(path);
  if (!format) {
    throw new DocCodecError('Unsupported document path.');
  }

  const doc = format === 'markdown' ? parseMarkdownToJson(raw) : parseJdocJson(raw);
  return { format, doc };
}

export function serializeDocForStorage(format: DocFormat, doc: JSONContent): string {
  return format === 'markdown' ? serializeJsonToMarkdown(doc) : serializeJdocJson(doc);
}

export function resolveSaveContentType(path: string, format: DocFormat, serverContentType?: string): string {
  if (serverContentType?.trim()) return serverContentType.trim();
  void path;
  return contentTypeForDocFormat(format);
}

/** Round-trip smoke helper for tests. */
export function roundTripMarkdown(markdown: string): string {
  const json = parseMarkdownToJson(markdown);
  return serializeJsonToMarkdown(json);
}

export function roundTripJdoc(raw: string): string {
  const json = parseJdocJson(raw);
  return serializeJdocJson(json);
}
